// src/student/pages/StudentResults.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Award,
  CheckCircle,
  Clock,
  Eye,
  RefreshCw,
  CheckSquare,
  PenTool,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";
import { useStudentAuth } from "../context/StudentAuthContext";

export default function StudentResults() {
  const { studentUser } = useStudentAuth();
  const [activeTab, setActiveTab] = useState("prelims"); // "prelims" | "mains"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [prelimsResults, setPrelimsResults] = useState([]);
  const [mainsResults, setMainsResults] = useState([]);

  // Detail Modal for Prelims Result
  const [selectedPrelims, setSelectedPrelims] = useState(null);

  // Detail Modal for Mains Evaluation
  const [selectedMains, setSelectedMains] = useState(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await studentPortalAPI.getMyResults(studentUser?.email);
      if (res && res.success) {
        const prelims = res.data?.prelims || res.prelimsResults || res.data?.prelimsResults || [];
        const mains = res.data?.mains || res.mainsResults || res.data?.mainsResults || [];
        setPrelimsResults(prelims);
        setMainsResults(mains);
      }
    } catch (err) {
      setError(err.message || "Failed to load student results.");
    } finally {
      setLoading(false);
    }
  }, [studentUser?.email]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-red-950 via-slate-900 to-[#0b1528] rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full">
              Performance Analytics
            </span>
            <span className="text-xs text-slate-300 font-medium">UPSC CSE</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            My Examination Results
          </h1>
          <p className="text-xs text-slate-300 mt-1 max-w-xl">
            Track your mock test scores, accuracy percentages, negative marking impact, and mentor feedback on Mains answer writing.
          </p>
        </div>

        <button
          onClick={fetchResults}
          className="self-start md:self-auto flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold backdrop-blur-xs transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>Refresh Results</span>
        </button>
      </div>

      {/* Centered Two-Option Tab Selector (Maroon & White) */}
      <div className="flex justify-center">
        <div className="bg-white p-1 rounded-2xl border border-slate-200/90 shadow-xs flex items-center gap-1">
          <button
            onClick={() => setActiveTab("prelims")}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold transition-all ${
              activeTab === "prelims"
                ? "bg-[#7b0000] text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <CheckSquare size={16} />
            <span>Prelims Results</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                activeTab === "prelims"
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {prelimsResults.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab("mains")}
            className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-xs font-bold transition-all ${
              activeTab === "mains"
                ? "bg-[#7b0000] text-white shadow-sm"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <PenTool size={16} />
            <span>Mains Results</span>
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                activeTab === "mains"
                  ? "bg-white/20 text-white"
                  : "bg-slate-100 text-slate-600"
              }`}
            >
              {mainsResults.length}
            </span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          Loading Examination Results...
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      ) : activeTab === "prelims" ? (
        /* PRELIMS RESULTS TABLE */
        prelimsResults.length === 0 ? (
          <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
            <Award className="mx-auto text-slate-300 mb-2" size={36} />
            <p className="text-sm font-semibold text-slate-700">No Prelims attempts yet</p>
            <p className="text-xs text-slate-400 mt-1">Take your first mock test from the Prelims Tests section.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase tracking-wider text-[10px]">
                    <th className="py-3.5 px-4">Test Title</th>
                    <th className="py-3.5 px-4">Date Attended</th>
                    <th className="py-3.5 px-3 text-center">Total Qs</th>
                    <th className="py-3.5 px-3 text-center text-emerald-700">Correct</th>
                    <th className="py-3.5 px-3 text-center text-rose-700">Incorrect</th>
                    <th className="py-3.5 px-3 text-center text-slate-500">Unanswered</th>
                    <th className="py-3.5 px-4 text-center">Score</th>
                    <th className="py-3.5 px-4 text-center">Accuracy</th>
                    <th className="py-3.5 px-4 text-right">Breakdown</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {prelimsResults.map((item) => (
                    <tr key={item._id} className="hover:bg-slate-50/70 transition-colors">
                      <td className="py-3.5 px-4 font-bold text-slate-900">
                        {item.testTitle || item.testId?.title || "Prelims Mock Test"}
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 font-medium">
                        {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString("en-IN") : "Recent"}
                      </td>
                      <td className="py-3.5 px-3 text-center font-bold text-slate-700">
                        {item.totalQuestions || 0}
                      </td>
                      <td className="py-3.5 px-3 text-center font-bold text-emerald-700">
                        {item.correctCount || 0}
                      </td>
                      <td className="py-3.5 px-3 text-center font-bold text-rose-700">
                        {item.incorrectCount || 0}
                      </td>
                      <td className="py-3.5 px-3 text-center font-bold text-slate-400">
                        {item.unansweredCount || 0}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className="font-extrabold text-slate-900 text-sm">
                          {item.marksObtained ?? 0}
                        </span>
                        <span className="text-[10px] text-slate-400"> / {item.totalMarks ?? 200}</span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className="px-2 py-0.5 rounded-full font-bold text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {item.percentage ? `${item.percentage}%` : "0%"}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <button
                          onClick={() => setSelectedPrelims(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-red-900 hover:text-white text-slate-700 text-xs font-semibold transition-colors"
                        >
                          <Eye size={13} />
                          <span>View Scorecard</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        /* MAINS RESULTS LIST */
        mainsResults.length === 0 ? (
          <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
            <PenTool className="mx-auto text-slate-300 mb-2" size={36} />
            <p className="text-sm font-semibold text-slate-700">No Mains submissions yet</p>
            <p className="text-xs text-slate-400 mt-1">Submit your first answer from the Mains Initiatives section.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {mainsResults.map((sub) => {
              const isEvaluated = sub.status === "evaluated" || sub.evaluationStatus === "published";
              return (
                <div
                  key={sub._id}
                  className="bg-white rounded-2xl border border-slate-200 hover:shadow-md transition-all p-5 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-red-900 bg-red-50 border border-red-100 px-2.5 py-0.5 rounded-full">
                        {sub.gsPaper || "GS General Studies"}
                      </span>
                      {isEvaluated ? (
                        <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <CheckCircle size={12} /> Evaluated
                        </span>
                      ) : (
                        <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Clock size={12} /> Under Review
                        </span>
                      )}
                    </div>

                    <h3 className="text-sm font-bold text-slate-900 mb-2">
                      {sub.initiativeTitle || sub.initiativeId?.title || "Mains Descriptive Answer"}
                    </h3>

                    <p className="text-xs text-slate-600 line-clamp-3 italic bg-slate-50 p-3 rounded-xl border border-slate-100 mb-3 font-serif">
                      "{sub.answerText || "Handwritten copy submitted."}"
                    </p>

                    <div className="flex items-center gap-3 text-xs text-slate-500 py-1">
                      <span>Word Count: <strong>{sub.wordCount || 0}</strong></span>
                      <span>•</span>
                      <span>Submitted: <strong>{sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString("en-IN") : "Recent"}</strong></span>
                    </div>

                    {/* Evaluator Marks & Feedback if Published */}
                    {isEvaluated && (
                      <div className="mt-3 p-3.5 bg-emerald-50/70 border border-emerald-200 rounded-xl space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-emerald-950">Faculty Evaluation</span>
                          <span className="text-sm font-black text-emerald-800">
                            {sub.marksAwarded ?? sub.marks ?? 0} Marks
                          </span>
                        </div>
                        {sub.evaluatorFeedback && (
                          <p className="text-xs text-emerald-900 leading-relaxed font-sans">
                            {sub.evaluatorFeedback}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button
                      onClick={() => setSelectedMains(sub)}
                      className="text-xs font-semibold text-slate-700 hover:text-red-900 transition-colors"
                    >
                      View Full Submission
                    </button>
                    {!isEvaluated && (
                      <span className="text-[11px] text-slate-400">Feedback will appear once published</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Prelims Scorecard Modal */}
      {selectedPrelims && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-200 space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="text-base font-bold text-slate-900">
                Scorecard: {selectedPrelims.testTitle || "Mock Test"}
              </h3>
              <button
                onClick={() => setSelectedPrelims(null)}
                className="text-slate-400 hover:text-slate-700 font-bold p-1 rounded"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="block text-[10px] text-slate-400 uppercase font-semibold">Marks Obtained</span>
                <span className="text-lg font-black text-red-900">{selectedPrelims.marksObtained ?? 0}</span>
                <span className="text-slate-400 text-[10px]"> / {selectedPrelims.totalMarks ?? 200}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="block text-[10px] text-slate-400 uppercase font-semibold">Overall Percentage</span>
                <span className="text-lg font-black text-emerald-700">
                  {selectedPrelims.percentage ? `${selectedPrelims.percentage}%` : "0%"}
                </span>
              </div>
              <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-100">
                <span className="block text-[10px] text-emerald-800 uppercase font-semibold">Correct Responses</span>
                <span className="text-lg font-black text-emerald-700">{selectedPrelims.correctCount ?? 0}</span>
                <span className="text-emerald-600 text-[10px] block">+2.00 marks each</span>
              </div>
              <div className="p-3 bg-rose-50/60 rounded-xl border border-rose-100">
                <span className="block text-[10px] text-rose-800 uppercase font-semibold">Incorrect Responses</span>
                <span className="text-lg font-black text-rose-700">{selectedPrelims.incorrectCount ?? 0}</span>
                <span className="text-rose-600 text-[10px] block">-0.66 penalty each</span>
              </div>
            </div>

            <button
              onClick={() => setSelectedPrelims(null)}
              className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold"
            >
              Close Scorecard
            </button>
          </div>
        </div>
      )}

      {/* Mains Submission Modal */}
      {selectedMains && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-2xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <span className="text-xs font-bold text-red-900 uppercase">
                {selectedMains.gsPaper} • Submission Review
              </span>
              <button
                onClick={() => setSelectedMains(null)}
                className="text-slate-400 hover:text-slate-700 font-bold p-1 rounded"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              <h3 className="text-base font-bold text-slate-900">
                {selectedMains.initiativeTitle || "Mains Answer Submission"}
              </h3>

              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs font-serif whitespace-pre-line text-slate-800 leading-relaxed">
                {selectedMains.answerText || "Handwritten PDF submitted."}
              </div>

              {selectedMains.evaluatorFeedback && (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-emerald-950">Faculty Marks & Comments</span>
                    <span className="text-sm font-extrabold text-emerald-800">
                      {selectedMains.marksAwarded ?? selectedMains.marks ?? 0} Marks
                    </span>
                  </div>
                  <p className="text-xs text-emerald-900 leading-relaxed">
                    {selectedMains.evaluatorFeedback}
                  </p>
                </div>
              )}
            </div>

            <div className="pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setSelectedMains(null)}
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
